import React from 'react';
import { ArrowRight, TrendingUp, TrendingDown, Gauge, DollarSign, Sparkles, Loader2, Zap } from 'lucide-react';

const signedMoney = (v) => {
  if (v == null) return '—';
  const sign = v >= 0 ? '+' : '−';
  const a = Math.abs(v);
  return a >= 1e6 ? `${sign}$${(a / 1e6).toFixed(1)}M` : `${sign}$${Math.round(a / 1e3)}K`;
};
const money = (v) => {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a < 1000) return '$0';
  return a >= 1e6 ? `$${(a / 1e6).toFixed(1)}M` : `$${Math.round(a / 1e3)}K`;
};

const BAND_TONE = {
  Strong: 'text-emerald-300 bg-emerald-400/10 ring-emerald-400/30',
  Healthy: 'text-teal-300 bg-teal-400/10 ring-teal-400/30',
  Watch: 'text-amber-300 bg-amber-400/10 ring-amber-400/30',
  'At Risk': 'text-rose-300 bg-rose-400/10 ring-rose-400/30',
};
const EXPO_TONE = { Low: '#10b981', Moderate: '#22d3ee', High: '#f59e0b', 'Very High': '#fb7185', Severe: '#f43f5e' };

const HeadlineTile = ({ icon: Icon, label, value, suffix, band, hint, delta, deltaLabel, testId }) => (
  <div className="rounded-2xl bg-[#111827] ring-1 ring-[#1f2a44] shadow-xl p-5 flex flex-col justify-between min-h-[132px]" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
        <Icon className="h-3.5 w-3.5 text-cyan-400" /> {label}
      </span>
      {band && (
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${BAND_TONE[band] || BAND_TONE.Watch}`}>{band}</span>
      )}
    </div>
    <div>
      <div className="flex items-end gap-2 flex-wrap">
        <span className="text-4xl font-heading font-extrabold tabular-nums text-cyan-300">{value}</span>
        {suffix && <span className="text-lg font-bold text-slate-400 ml-0.5">{suffix}</span>}
        {delta != null && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-bold mb-1.5 tabular-nums ${delta >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid={`${testId}-qoq`}>
            {delta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {delta > 0 ? '+' : ''}{delta} pts {deltaLabel || 'QoQ'}
          </span>
        )}
      </div>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  </div>
);

const StageChip = ({ label, score, isRoi, isFirst, isLast }) => (
  <div className={`shrink-0 rounded-xl px-3.5 py-2.5 text-center ring-1 ${
    isLast ? 'bg-teal-400/10 ring-teal-400/30' : isFirst ? 'bg-amber-400/10 ring-amber-400/30' : 'bg-white/[0.05] ring-white/10'
  }`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    <p className={`text-lg font-extrabold tabular-nums ${isLast ? 'text-teal-300' : isFirst ? 'text-amber-300' : 'text-slate-100'}`}>
      {score == null ? '—' : isRoi ? `${score}x` : Math.round(score)}
    </p>
  </div>
);

const HandoffConnector = ({ handoff }) => {
  if (!handoff) return <ArrowRight className="h-4 w-4 text-slate-600 shrink-0 mx-1" />;
  const amp = handoff.pct >= 0;
  const d = handoff.delta;
  return (
    <div className="flex flex-col items-center shrink-0 px-1.5" data-testid={`chain-handoff-${handoff.key}`}>
      <span className={`inline-flex items-center gap-0.5 text-xs font-bold tabular-nums ${amp ? 'text-emerald-300' : 'text-rose-300'}`}>
        {amp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {handoff.pct > 0 ? '+' : ''}{handoff.pct}%
        {d != null && d !== 0 && (
          <span className={`ml-0.5 inline-flex items-center text-[9px] font-bold ${d > 0 ? 'text-emerald-400' : 'text-rose-400'}`}
            title={`${d > 0 ? '+' : ''}${d} pts vs last quarter`} data-testid={`chain-handoff-${handoff.key}-trend`}>
            {d > 0 ? '▲' : '▼'}{Math.abs(d)}
          </span>
        )}
      </span>
      <ArrowRight className={`h-4 w-4 my-0.5 ${amp ? 'text-emerald-500/70' : 'text-rose-500/70'}`} />
      {handoff.usd != null && (
        <span className={`text-[10px] font-semibold tabular-nums ${amp ? 'text-emerald-400/90' : 'text-rose-400/90'}`}>
          {signedMoney(handoff.usd)}
        </span>
      )}
    </div>
  );
};

export const NavigationChainHealth = ({ data, loading, cohorts = [], selectedCohort = 'all', onCohortChange, isDark, navigate }) => {
  const ch = data || {};
  const stages = ch.stages || [];
  const handoffs = ch.handoffs || [];
  const handoffByIndex = {};
  handoffs.forEach((h, i) => { handoffByIndex[i] = h; });
  // Biggest leaking hand-off (most negative %) — powers the "Fix First" CTA.
  const worstLeak = handoffs
    .filter((h) => h.pct < 0)
    .sort((a, b) => a.pct - b.pct)[0] || null;
  const mm = ch.maturity_model;

  return (
    <div className={isDark ? 'home-dark-scope' : 'mc-light'} data-testid="navigation-chain-health-scope">
      <div className="rounded-2xl bg-[#0f1729] ring-1 ring-[#22304d] p-5 md:p-6 shadow-xl space-y-5" data-testid="nav-chain-health">
        {/* Header + cohort filter */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">Learning Value Transfer Chain</p>
            {cohorts.length > 0 && (
              <select
                value={selectedCohort}
                onChange={(e) => onCohortChange?.(e.target.value)}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#111827] text-slate-200 ring-1 ring-[#1f2a44] focus:outline-none shrink-0"
                data-testid="nav-chain-cohort-filter"
              >
                <option value="all">Whole organization</option>
                {cohorts.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
            )}
          </div>
          <h2 className="text-lg font-bold text-slate-100">Chain Health — where value amplifies or leaks</h2>
          <p className="text-xs text-slate-400">How value carries from Maturity through to Financial impact. Positive hand-offs amplify value; negative hand-offs leak it.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <>
            {/* Two headline numbers side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <HeadlineTile icon={Gauge} label="Chain Transfer Efficiency"
                value={ch.chain_transfer_efficiency == null ? '—' : ch.chain_transfer_efficiency} suffix={ch.chain_transfer_efficiency == null ? '' : '%'}
                band={ch.chain_transfer_band} hint="Value flowing Learning → Financial"
                delta={ch.chain_transfer_efficiency_delta} deltaLabel="vs last quarter" testId="nav-chain-eff-tile" />
              <HeadlineTile icon={DollarSign} label="ROI Health"
                value={ch.roi_health?.multiple == null ? '—' : ch.roi_health.multiple} suffix={ch.roi_health?.multiple == null ? '' : 'x'}
                band={ch.roi_health?.band} hint="Blended return across business cases" testId="nav-chain-roi-tile" />
            </div>

            {/* Maturity condition strip — the cross-cutting environmental factor. */}
            {mm && (
              <div className="rounded-xl bg-gradient-to-r from-amber-500/10 to-rose-500/10 ring-1 ring-amber-400/25 p-4 flex flex-wrap items-center justify-between gap-4" data-testid="nav-chain-maturity-strip">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-[#111827] ring-1 ring-amber-400/30 px-4 py-2 text-center">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-amber-300">Maturity Health</p>
                    <p className="text-2xl font-extrabold tabular-nums text-amber-300 leading-tight">{mm.score}</p>
                    <p className="text-[9px] text-slate-400">{mm.band} conditions</p>
                  </div>
                  <p className="text-xs text-slate-300 max-w-md">Maturity is a cross-cutting condition — it erodes value at <span className="font-semibold text-slate-100">every</span> hand-off, not just Learning, and its bite grows downstream.</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Value at Risk (maturity)</p>
                    <p className="text-xl font-extrabold tabular-nums text-rose-300">{money(mm.value_at_risk_usd)}</p>
                    <p className="text-[9px] text-slate-400">{mm.value_at_risk_pct}% of potential</p>
                  </div>
                </div>
              </div>
            )}

            {/* Chain visual (left) + Fix-First Take Action tile (right) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
              <div className="lg:col-span-2 rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] p-4 overflow-x-auto">
                <div className="flex items-center min-w-max">
                  {stages.map((s, i) => (
                    <React.Fragment key={s.key}>
                      <StageChip label={s.label} score={s.score} isRoi={s.is_roi} isFirst={i === 0} isLast={i === stages.length - 1} />
                      {i < stages.length - 1 && <HandoffConnector handoff={handoffByIndex[i]} />}
                    </React.Fragment>
                  ))}
                </div>
                {/* Maturity exposure overlay — maturity affects EVERY stage, rising downstream. */}
                {mm && (
                  <div className="mt-4 pt-3 border-t border-[#1f2a44]" data-testid="nav-chain-maturity-exposure">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-amber-300">Maturity Exposure — impact at each stage</span>
                      <span className="text-[10px] text-slate-500">rises as value moves downstream</span>
                    </div>
                    <div className="h-2 rounded-full mb-2" style={{ background: 'linear-gradient(90deg,#10b981,#22d3ee,#f59e0b,#f43f5e)' }} />
                    <div className="flex min-w-max">
                      {mm.stages.map((s) => {
                        const eroded = ch.value_pool ? Math.round(ch.value_pool * s.exposure / 100) : null;
                        const tip = `${s.label}: maturity sensitivity ${Math.round((s.sensitivity || 0) * 100)}%. `
                          + `At Maturity ${mm.score}, ~${s.exposure}% of value is exposed here`
                          + (eroded ? ` (~${money(eroded)} eroded).` : '.')
                          + ` Exposure ${s.band}.`;
                        return (
                          <div key={s.label} className="flex-1 min-w-[90px] text-center cursor-help" title={tip}
                            data-testid={`nav-chain-exposure-${s.label.toLowerCase()}`}>
                            <p className="text-sm font-extrabold tabular-nums" style={{ color: EXPO_TONE[s.band] || '#f59e0b' }}>{s.exposure}%</p>
                            <p className="text-[9px] text-slate-500">{s.label} · {s.band}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
                  Hand-off efficiency (%), the $ values and the AI read below are all scoped to the selected cohort. Maturity conditions erode a growing share of value at each downstream stage.
                </p>
              </div>

              {/* Fix First — biggest leaking hand-off → matching business case.
                  Mirrors the "Top Priority · Take Action" tile on the health pages. */}
              <div className={`relative overflow-hidden rounded-xl p-5 flex flex-col ${worstLeak ? 'bg-[#111827] ring-1 ring-rose-500/40 shadow-lg shadow-rose-900/20' : 'bg-[#111827] ring-1 ring-[#1f2a44]'}`} data-testid="nav-chain-fix-first">
                {worstLeak && <div className="absolute -top-6 -right-6 h-20 w-20 rounded-full bg-rose-500/10 blur-2xl" />}
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-rose-300">
                  <TrendingDown className="h-3.5 w-3.5 text-rose-400" /> Fix First — biggest leak
                </div>
                {worstLeak ? (
                  <>
                    <p className="mt-2 text-lg font-bold text-slate-100 leading-tight">{worstLeak.from} → {worstLeak.to}</p>
                    <p className="mt-1 text-sm text-rose-300 font-semibold tabular-nums">
                      {worstLeak.pct}% leakage{worstLeak.usd != null ? <span className="text-slate-400 font-normal"> · {signedMoney(worstLeak.usd)}</span> : null}
                    </p>
                    <p className="mt-2 text-xs text-slate-400 flex-1">
                      The steepest value drop in the chain.
                      {ch.fix_first_case?.name
                        ? <> Open <span className="text-slate-200 font-semibold">{ch.fix_first_case.name}</span> ({ch.fix_first_case.roi_percent}% ROI) — the weakest case to strengthen.</>
                        : ' Open the matching business case to plug it.'}
                    </p>
                    {navigate && (
                      <button
                        onClick={() => navigate(ch.fix_first_case?.id ? `/roi-health/deep-dives/${encodeURIComponent(ch.fix_first_case.id)}` : '/roi-health/cases')}
                        data-testid="nav-chain-fix-first-action"
                        className="mt-4 w-full justify-center inline-flex items-center gap-2 text-sm font-bold px-3 py-2.5 rounded-lg bg-rose-500 text-white hover:bg-rose-400 shadow-lg shadow-rose-500/30 transition-colors">
                        <Zap className="h-4 w-4" /> Take Action <ArrowRight className="h-4 w-4" />
                      </button>
                    )}
                  </>
                ) : (
                  <p className="mt-2 text-sm text-emerald-300 font-semibold flex-1">No leaking hand-offs — value is carrying through the chain.</p>
                )}
              </div>
            </div>

            {/* Value pool / leaked / amplified — with a clear empty state when a
                cohort has no modelled business cases (value pool = 0). */}
            {ch.value_pool ? (
              <div className="grid grid-cols-3 gap-4">
                <div className="rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Value Pool</p>
                  <p className="text-xl font-extrabold tabular-nums text-slate-100 mt-1">{money(ch.value_pool)}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Modelled value flowing through the chain</p>
                </div>
                <div className="rounded-xl bg-[#111827] ring-1 ring-rose-400/20 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-rose-400">Value Leaked</p>
                  <p className="text-xl font-extrabold tabular-nums text-rose-300 mt-1">{ch.net_leak_usd ? money(ch.net_leak_usd) : '$0'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Across leaking hand-offs</p>
                </div>
                <div className="rounded-xl bg-[#111827] ring-1 ring-emerald-400/20 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Value Amplified</p>
                  <p className="text-xl font-extrabold tabular-nums text-emerald-300 mt-1">{ch.net_amplify_usd ? money(ch.net_amplify_usd) : '$0'}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Across amplifying hand-offs</p>
                </div>
              </div>
            ) : (
              <div className="rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] p-4 text-center" data-testid="nav-chain-empty-pool">
                <p className="text-sm text-slate-300 font-semibold">No cohort-specific business cases modelled for this cohort</p>
                <p className="text-xs text-slate-400 mt-1">The hand-off efficiency above is org-level. Add a saved scenario tagged to this cohort to see its $ value pool, leakage and amplification.</p>
              </div>
            )}

            {/* Maturity drivers + uplift callout */}
            {mm && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="nav-chain-maturity-drivers">
                <div className="rounded-xl bg-[#111827] ring-1 ring-[#1f2a44] p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Top Maturity Drivers</p>
                  <div className="space-y-1.5">
                    {mm.drivers.map((d, i) => (
                      <button key={d.name} onClick={() => navigate && navigate(`/maturity-health?pillar=${encodeURIComponent(d.name)}`)}
                        className="w-full flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-white/5 transition-colors text-left group"
                        data-testid={`nav-chain-driver-${i}`} title={`Open ${d.name} on Maturity Health`}>
                        <span className="w-4 h-4 rounded-full bg-amber-400/20 text-amber-300 text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span className="flex-1 text-xs text-slate-300 truncate group-hover:text-slate-100">{d.name}</span>
                        <span className="text-xs font-bold tabular-nums text-amber-300">{d.weight}%</span>
                        <ArrowRight className="h-3 w-3 text-slate-600 group-hover:text-amber-300 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-2 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 ring-1 ring-emerald-400/25 p-4 flex flex-col justify-between">
                  <div>
                    <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300 mb-1"><Zap className="h-3.5 w-3.5" /> Maturity is constraining value realization</p>
                    <p className="text-sm text-slate-200">Lifting maturity to <span className="font-bold">Good (70)</span> could unlock an estimated <span className="font-extrabold text-emerald-300">{money(mm.uplift_value_usd)}</span> in value realization{mm.uplift_roi ? <> and <span className="font-extrabold text-emerald-300">+{mm.uplift_roi}x</span> ROI potential</> : null}.</p>
                  </div>
                  {navigate && (
                    <button onClick={() => navigate('/maturity-health')} data-testid="nav-chain-maturity-priorities"
                      className="mt-3 self-start inline-flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-400 shadow-lg shadow-emerald-500/25 transition-colors">
                      View Priorities &amp; Actions <ArrowRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Cached AI narrative */}
            {ch.narrative && (
              <div className="rounded-xl bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 ring-1 ring-indigo-400/20 p-4" data-testid="nav-chain-narrative">
                <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-cyan-300 mb-1.5">
                  <Sparkles className="h-3.5 w-3.5" /> AI read — where value is leaking
                </p>
                <p className="text-sm leading-relaxed text-slate-200 whitespace-pre-line">{ch.narrative}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NavigationChainHealth;
